import { environment } from '../../../environments/environment';

/**
 * TEMPORARY DIAGNOSTIC — last-tab-out discard / liveness probe.
 *
 * Answers one question: when a freshly opened tab did NOT discard a
 * conversion's abandoned edits, which of the three conditions in
 * `FilingFormStateService.shouldDiscardAsLastTabOut` failed, and — if a sibling
 * vouched — WHICH tab answered and what was it holding at the time.
 *
 * Why this cannot be a `console.log`: the tabs under investigation are CLOSED
 * as part of the repro, taking their devtools console with them. Records are
 * therefore written to `localStorage` and read back later from whichever tab is
 * still open (the XBRL Files listing).
 *
 * **Per-tab keys, merged on read.** Every tab writes to its own
 * `<PREFIX><tabId>` key rather than appending to one shared array. Concurrent
 * tabs doing read-modify-write on a single key silently lose each other's
 * entries — which is precisely the interleaving this diagnostic exists to
 * capture. {@link dumpLivenessDiagnostics} merges every key and sorts by
 * timestamp to rebuild the true cross-tab timeline.
 *
 * Storage key prefix is deliberately OUTSIDE the `mbrs-` / `__mbrs-`
 * namespaces that `clearCustomerDataCache()` wipes on logout, so a session
 * boundary mid-test does not erase the evidence. Same reasoning as
 * `session-timing.service.ts`'s `yyc-session-tracking`.
 *
 * **Two gates, both must pass.** A build-time `!environment.production` check
 * (so the whole harness is dead-code-eliminated from a production bundle) and
 * the runtime `livenessProbeDebug` flag from `/assets/config.json`, pushed in
 * once at boot via {@link setLivenessDiagnosticsEnabled}. The runtime half
 * exists so ops can silence the harness on a non-prod VM — UAT especially —
 * by editing `/etc/mbrs/frontend-overrides.json` and re-deploying, without
 * waiting for a rebuild. See `docs/deployment/runtime-config.md`
 * *`livenessProbeDebug`*.
 *
 * DI-free and gated to non-production. **Remove this file, its call sites in
 * `filing-form-state.service.ts` / `cross-tab-sync.service.ts` /
 * `xbrl-files.component.ts`, and this note once the probe defect is closed.**
 */

/** One step in the cross-tab timeline. */
export type LivenessDiagnosticEvent =
  /** `setConversionId` began hydrating a conversion in this tab. */
  | 'hydrate:start'
  /** The hydration branch that actually ran (refresh-discard / last-tab-out / keep). */
  | 'hydrate:branch'
  /** This tab flipped `hasSettledConversionOwnership` — it may now vouch. */
  | 'ownership:settled'
  /** This tab broadcast a `livenessPing` and started its timeout. */
  | 'probe:sent'
  /** The probe resolved — `alive: true` (someone answered) or `false` (timed out). */
  | 'probe:result'
  /** A `livenessPing` arrived and this tab decided whether to answer it. */
  | 'ping:received'
  /** This tab armed the per-tab unload-intent marker on `pagehide`. */
  | 'unload:armed'
  /**
   * Per-sheet abandonment protocol (`sheetReleased` / `sheetHeldPong`, added by
   * `a481c54`). These four postdate the original harness and exist for the
   * multi-sheet variant of the defect: a tab that dirties two sheets releases
   * only the one it had on screen, so the timeline must show WHICH sheet was
   * named and which were silently left behind.
   */
  /** This tab broadcast a `sheetReleased`, or declined to. */
  | 'sheet:release-sent'
  /** A `sheetReleased` arrived and this tab vouched / scheduled / ignored it. */
  | 'sheet:release-received'
  /** A grace timer expired and this tab reverted the sheet to baseline. */
  | 'sheet:discarded'
  /** The container registered or withdrew its mounted-sheet probe. */
  | 'sheet:probe';

export interface LivenessDiagnosticRecord {
  /** Epoch millis — the sort key that rebuilds the cross-tab order. */
  t: number;
  /** ISO form of `t`, so a dumped table is readable without conversion. */
  iso: string;
  /** Which tab wrote this line. Stable for the life of the JS context. */
  tab: string;
  event: LivenessDiagnosticEvent;
  conversionId?: string;
  /** Event-specific payload — the conditions, the verdict, the sender tab id. */
  detail?: Record<string, unknown>;
}

const KEY_PREFIX = 'yyc-liveness-probe-diag:';

/**
 * Ring-buffer ceiling per tab. Generous enough to hold several repro rounds,
 * small enough that a forgotten diagnostic cannot grow the quota unbounded.
 */
const MAX_RECORDS_PER_TAB = 300;

/**
 * Per-JS-context tab id. Deliberately generated HERE rather than read from
 * `CrossTabSyncService.getTabId()`: that service is hand-stubbed in many specs
 * across the suite, and a stub missing the method would fail those specs at
 * runtime rather than at compile time. A diagnostic must not be able to do
 * that. The two ids are different values, which is fine — correlation inside
 * this timeline is what matters, and `ping:received` records the SENDER's
 * transport id separately.
 */
const TAB_ID: string = (() => {
  const cryptoRef = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;

  if (cryptoRef && typeof cryptoRef.randomUUID === 'function')
    return `diag-${cryptoRef.randomUUID().slice(0, 8)}`;

  return `diag-${Math.random().toString(36).slice(2, 10)}`;
})();

/**
 * Runtime half of the gate, pushed in from the `APP_INITIALIZER` in
 * `app.config.ts` once `RuntimeConfigService.load()` has resolved.
 *
 * Defaults to `false` — a module-level default cannot read runtime config, and
 * a harness that records before anyone asked for it is exactly the residue this
 * flag exists to prevent. Nothing on the recorded paths runs before bootstrap
 * completes, so no real record is lost to the default. Specs that want the
 * harness call {@link setLivenessDiagnosticsEnabled} themselves.
 */
let runtimeEnabled = false;

/**
 * Set the runtime half of the gate. Called once from the `APP_INITIALIZER`
 * factory in `app.config.ts` with `RuntimeConfigService.livenessProbeDebug()`.
 *
 * Deliberately a plain setter rather than a `RuntimeConfigService` import: this
 * module is called from `FilingFormStateService` and `CrossTabSyncService`,
 * both of which are hand-stubbed in dozens of specs, and a diagnostic must not
 * be able to fail the suites it observes. Same reasoning as `TAB_ID` below.
 */
export function setLivenessDiagnosticsEnabled(enabled: boolean): void {
  runtimeEnabled = enabled;
}

/**
 * Whether the harness is recording. Exported so a call site that wants to
 * advertise the console helpers (the XBRL Files listing) can stay silent when
 * the flag is off.
 */
export function isLivenessDiagnosticsEnabled(): boolean {
  return isEnabled();
}

function isEnabled(): boolean {
  return !environment.production && runtimeEnabled && typeof localStorage !== 'undefined';
}

/**
 * Append one record for this tab. Never throws — a diagnostic that can break
 * the path it observes is worse than no diagnostic.
 */
export function recordLivenessDiagnostic(
  event: LivenessDiagnosticEvent,
  conversionId?: string,
  detail?: Record<string, unknown>
): void {
  if (!isEnabled()) return;

  try {
    const key = `${KEY_PREFIX}${TAB_ID}`;
    const existing = readTabRecords(key);
    const timestamp = Date.now();

    existing.push({ t: timestamp, iso: new Date(timestamp).toISOString(), tab: TAB_ID, event, conversionId, detail });

    // Drop from the FRONT — the tail is the interesting end of a repro.
    while (existing.length > MAX_RECORDS_PER_TAB)
      existing.shift();

    localStorage.setItem(key, JSON.stringify(existing));
  }
  catch {
    // Quota, private mode, serialization — all non-fatal for a diagnostic.
  }
}

/** Read + merge every tab's buffer into one timeline, oldest first. */
export function readLivenessDiagnostics(): LivenessDiagnosticRecord[] {
  if (!isEnabled()) return [];

  const merged: LivenessDiagnosticRecord[] = [];

  try {
    for (let storageIndex = 0; storageIndex < localStorage.length; storageIndex++) {
      const key = localStorage.key(storageIndex);

      if (key?.startsWith(KEY_PREFIX))
        merged.push(...readTabRecords(key));
    }
  }
  catch {
    return merged;
  }

  return merged.sort((first, second) => first.t - second.t);
}

/**
 * Print the merged timeline. Call from any surviving tab's console via
 * `window.__livenessDiag()` (wired in the XBRL Files listing) — the tabs being
 * diagnosed are closed by the time anyone reads this.
 */
export function dumpLivenessDiagnostics(): LivenessDiagnosticRecord[] {
  const records = readLivenessDiagnostics();

  if (!isEnabled()) {
    console.log(
      '[LivenessDiag] disabled — set "livenessProbeDebug": true in the runtime config '
      + '(config.dev.local.json locally, /etc/mbrs/frontend-overrides.json on a VM) and reload.',
    );
    return records;
  }

  if (!records.length) {
    console.log('[LivenessDiag] no records — reproduce first, then dump again.');
    return records;
  }

  console.log(`[LivenessDiag] ${records.length} record(s) across all tabs, oldest first:`);
  console.table(
    records.map(record => ({
      iso: record.iso,
      tab: record.tab,
      event: record.event,
      conversionId: record.conversionId ?? '',
      detail: record.detail ? JSON.stringify(record.detail) : '',
    }))
  );

  return records;
}

/** Wipe every tab's buffer. Run before each repro round for a clean timeline. */
export function clearLivenessDiagnostics(): void {
  if (!isEnabled()) {
    console.log('[LivenessDiag] disabled — nothing recorded to clear.');
    return;
  }

  try {
    const keys: string[] = [];

    for (let storageIndex = 0; storageIndex < localStorage.length; storageIndex++) {
      const key = localStorage.key(storageIndex);

      if (key?.startsWith(KEY_PREFIX))
        keys.push(key);
    }

    keys.forEach(key => localStorage.removeItem(key));
    console.log(`[LivenessDiag] cleared ${keys.length} tab buffer(s).`);
  }
  catch {
    // Non-fatal.
  }
}

function readTabRecords(key: string): LivenessDiagnosticRecord[] {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  }
  catch {
    return [];
  }
}
